import { Component, Inject, Injectable, ViewEncapsulation } from "@angular/core";
import { ILoadingSpinnerConfig, loadingSpinnerConfigInjectionToken } from "moo-shared";
import { forkJoin, interval, Observable, of } from "rxjs";
import { delay, distinctUntilChanged, first, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { LoadingService } from "./loading.service";
//system detects loading-spinner component form moo libraries so give here a diferent name
@Component({
  selector: "au-loading-spinner",
  templateUrl: "./loading-spinner.component.html",
  styleUrls: ["./loading-spinner.component.scss"],
  encapsulation: ViewEncapsulation.None
})
export class LoadingSpinnerComponent {
  showSpinner$: Observable<boolean>;
  blockInteraction$: Observable<boolean>;

  constructor(public loader: LoadingService,
    @Inject(loadingSpinnerConfigInjectionToken) private readonly loadingSpinnerConfig: ILoadingSpinnerConfig) {

    this.blockInteraction$ = loader.loading$.pipe(
      map(x => x && this.loadingSpinnerConfig.isEnabled())
    );

    this.showSpinner$ = this.blockInteraction$.pipe(
      debounceSpinner(500, 500)
    );

  }
}

@Injectable({
  providedIn: "root"
})
export class LoadingSpinnerConfig implements ILoadingSpinnerConfig {

  private enabled = true;

  isEnabled(): boolean {
    return this.enabled;
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }
}


// Exported for testing in isolation
export function debounceSpinner(debounceTimeMs: number, minimumShowTimeMs: number) {

  let showTimer$ = of(null);
  let isInMinimumShowTime = false;

  function resetShowTime() {

    // Replace the current show timer with a new one
    showTimer$ = of(null).pipe(
      delay(minimumShowTimeMs),
      shareReplay(1)
    );

    isInMinimumShowTime = true;

    // Start the timer immediately, it unsubscribes automatically when the timer completes
    showTimer$.subscribe(() => {
      isInMinimumShowTime = false;
    });
  }

  return (source: Observable<boolean>) => {
    return source.pipe(

      distinctUntilChanged(), // multiple true vaules do not reset the debounce

      switchMap(x => // a false value within the debounce time cancels the true emission
        x ?
          interval(debounceTimeMs).pipe(first(), map(() => x)) : // true values are debounced
          of(x)),

      distinctUntilChanged(), // Merge identical values if these are emitted due to the timing of the debounce

      tap(x => {  // The minimum show timer starts running on a true value, and doesn't reset until its done
        if (x && !isInMinimumShowTime) {
          resetShowTime();
        }
      }),

      switchMap(x =>
        x ?
          of(x) :
          forkJoin({ showTimer$, value: of(x) }).pipe(map(x => x.value))), // false is only emitted when the show timer has completed

      distinctUntilChanged() // Merge identical values if these are emitted due to the timing of the minimum show time
    );
  };
}
